import React, { FC, ChangeEvent, KeyboardEvent, useRef, useEffect, useCallback } from "react";
import { useFormContext } from "react-hook-form";

import "./Checkbox.scss";

interface BaseProps {
  /**
   * Indicates if the checkbox is required.
   */
  required?: boolean;
  /**
   * Disables the checkbox if true.
   */
  disabled?: boolean;
  /**
   * Label text for the checkbox.
   */
  label?: React.ReactNode;
  /**
   * Additional supporting text below the label.
   */
  supportingText?: string;
  /**
   * Size of the checkbox, can be 'sm' or 'md'.
   */
  size?: "sm" | "md";
  /**
   * Indicates if the checkbox is checked.
   */
  checked?: boolean;
  /**
   * Indicates if the checkbox is in an indeterminate state.
   */
  indeterminate?: boolean;
}

interface WithReactHookFormProps extends BaseProps {
  /**
   * The name attribute for the checkbox, used as a key for form data.
   */
  name: string;
  /**
   * RegisterOptions object for react-hook-form.
   */
  registerOptions?: object;
}

interface WithoutReactHookFormProps extends BaseProps {
  /**
   * The name prop is set to never, indicating it should not be used when not using react-hook-form.
   */
  name?: never;
  /**
   * Callback for change events.
   */
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  /**
   * Default checked state for the checkbox.
   */
  defaultChecked?: boolean;
  /**
   * To ensure the checked state is controlled and has a single source of truth, the checked prop must be set.
   */
  checked?: boolean;
}

export type CheckboxProps = WithReactHookFormProps | WithoutReactHookFormProps;

const Checkbox: FC<CheckboxProps> = function (props) {
  const {
    name,
    checked,
    indeterminate = false,
    required = false,
    disabled = false,
    label = "",
    supportingText = "",
    size = "md",
  } = props;

  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  // Check if the component is using react-hook-form for form management
  const formContext = useFormContext();
  const isUsingReactHookForm = typeof name === "string" && !!formContext?.register;

  // Handle change events for the checkbox if not using react-hook-form
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { onChange } = props as WithoutReactHookFormProps;
    if (onChange) onChange(event);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (disabled || event.key !== "Enter") return;

    event.preventDefault();
    event.currentTarget.click();
  };

  // Merge refs for react-hook-form's register and our local ref
  const setRef = useCallback(
    (rhfRef?: (el: HTMLInputElement | null) => void) => (el: HTMLInputElement | null) => {
      inputRef.current = el;
      rhfRef?.(el);
    },
    [],
  );

  // Base props for the input element
  const inputProps: Record<string, unknown> = {
    type: "checkbox",
    checked,
    disabled,
    onChange: handleChange,
    onKeyDown: handleKeyDown,
    ref: setRef(),
  };

  // Set default checked state if not using react-hook-form
  if (!isUsingReactHookForm) {
    const { defaultChecked = false, checked } = props as WithoutReactHookFormProps;
    if (typeof checked === "boolean") Object.assign(inputProps, { checked });
    else Object.assign(inputProps, { defaultChecked });
  }

  // Register the checkbox with react-hook-form if it is being used
  if (isUsingReactHookForm) {
    const { registerOptions = {} } = props as WithReactHookFormProps;
    const { register, getValues } = formContext || {};
    const { ref: rhfRef, ...rest } = register(name!, { required, ...registerOptions });
    Object.assign(inputProps, {
      ...rest,
      ref: setRef(rhfRef),
      ...(typeof checked === "boolean" ? { checked } : { defaultChecked: getValues(name!) }),
    });
  }

  return (
    <div className={`checkbox-container checkbox-${size}`}>
      <label className="checkbox-label">
        <span className="checkbox-control">
          <input className="checkbox-v2" {...inputProps} />
        </span>
        <div className="checkbox-label-text-container">
          {label && <p className="checkbox-label-text text-primary">{label}</p>}
          {supportingText && <p className="checkbox-supporting-text">{supportingText}</p>}
        </div>
      </label>
    </div>
  );
};

export default Checkbox;
